import { useCallback, useMemo } from 'react';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';

import { ensureSortParam } from './ensureSortParam';
import { isDefaultSortOrdering } from './isDefaultSortOrdering';
import { withoutUnavailableSort } from './lastEvaluatedSort';
import { type SortOrdering } from './types';

export const cacheKey = 'flag-list-sort-ordering';

export function useCachedFlagListSortOrdering() {
  const [rawValue, slot] = useStorageSlot(cacheKey, {
    storageType: 'sessionStorage',
    isEnabled: true,
  });

  const cached = useMemo(() => {
    if (!rawValue) {
      return undefined;
    }

    if (isDefaultSortOrdering(rawValue)) {
      return undefined;
    }

    try {
      return withoutUnavailableSort(ensureSortParam(rawValue));
    } catch {
      return undefined;
    }
  }, [rawValue]);

  const put = useCallback(
    (value?: SortOrdering) => {
      if (isDefaultSortOrdering(value)) {
        slot.remove();
        return;
      }

      try {
        slot.set(ensureSortParam(value));
      } catch {
        slot.remove();
      }
    },
    [slot],
  );

  const value = useMemo(
    () => ({
      value: cached,
      put,
    }),
    [cached, put],
  );

  return value;
}
